import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';
import type { LoSiteCtaLayout } from '@/lib/lyrraone-site-theme-profiles';
import type { LoSiteElementStyle } from '@/lib/lyrraone-site-elements';
import {
  buttonStyleToCss,
  hasCustomButtonStyle,
} from '@/lib/lyrraone-site-elements';
import { resolveLoSiteLink } from '@/lib/lyrraone-site-links';
import { resolveLoBlockLink, type LoBlockLinkContext } from '@/lib/lyrraone-block-links';
import { SiteThemeLink, ThemeButtonText, ThemeEditable } from '@/components/lyrraone/site-themes/LoSiteThemeEditable';
import { cn } from '@/lib/utils';

interface LoSiteThemedCtaProps {
  layout: LoSiteCtaLayout;
  block: LoSiteBlock;
  blockKey: string;
  siteSlug: string;
  customDomainSlug?: string | null;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  styleOf: (key: string) => LoSiteElementStyle | undefined;
  linkContext?: LoBlockLinkContext;
}

function CtaButton(props: Omit<LoSiteThemedCtaProps, 'layout' | 'blockKey'>) {
  const { block, siteSlug, customDomainSlug, editMode, selectedElementKey, onSelectElement, onInlineTextChange, styleOf, linkContext } = props;
  if (!block.url) return null;
  const href = linkContext
    ? resolveLoBlockLink(block.url, linkContext)
    : resolveLoSiteLink(siteSlug, block.url, customDomainSlug);

  return (
    <ThemeEditable
      elementKey="cta.button"
      editMode={editMode}
      selectedElementKey={selectedElementKey}
      onSelectElement={onSelectElement}
      as="span"
      className="inline-block"
    >
      <SiteThemeLink
        href={href}
        className={cn('lo-btn lo-btn-primary lo-st-btn', hasCustomButtonStyle(styleOf('cta.button')) && 'lo-btn-custom')}
        style={buttonStyleToCss(styleOf('cta.button'))}
        editMode={editMode}
        onClick={() => onSelectElement?.('cta.button')}
      >
        <ThemeButtonText
          elementKey="cta.button"
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
        >
          {block.text ?? 'En savoir plus'}
        </ThemeButtonText>
      </SiteThemeLink>
    </ThemeEditable>
  );
}

export function LoSiteThemedCta({
  layout,
  block,
  blockKey,
  siteSlug,
  customDomainSlug,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  styleOf,
  linkContext,
}: LoSiteThemedCtaProps) {
  const shared = {
    block,
    siteSlug,
    customDomainSlug,
    editMode,
    selectedElementKey,
    onSelectElement,
    onInlineTextChange,
    styleOf,
    linkContext,
  };

  if (layout === 'band') {
    return (
      <section key={blockKey} className="lo-st-cta lo-st-cta--band">
        <div className="lo-wrap lo-st-cta__band-inner">
          <CtaButton {...shared} />
        </div>
      </section>
    );
  }

  if (layout === 'inline') {
    return (
      <section key={blockKey} className="lo-st-cta lo-st-cta--inline">
        <div className="lo-wrap lo-st-cta__inline-inner">
          <span className="lo-st-cta__inline-label">Prêt à commencer ?</span>
          <CtaButton {...shared} />
        </div>
      </section>
    );
  }

  return (
    <section key={blockKey} className="lo-st-cta lo-st-cta--centered">
      <div className="lo-wrap lo-st-cta__center">
        <CtaButton {...shared} />
      </div>
    </section>
  );
}
